import { Button } from "@mui/material";
import { alpha } from "@mui/material";
import { COLOR_BLACK, COLOR_GREY_BORDER, COLOR_WHITE } from "helpers/colors";
import { downloadCsv } from "helpers/downloadCsv";
import { FONT_FAMILY_GRAPHIK } from "helpers/FONT";
import { SPACING, SPACING_HALF } from "helpers/spacings";
import { Download } from "mdi-material-ui";
import StatsTable, {
  StatsTableButtonRow,
  StatsTableCell,
  StatsTableHeaderCell,
  StatsTableRow,
} from "molecules/StatsTable";
import React, { useMemo, useState } from "react";

import StatKeyPanel, { HeaderButton } from "./StatKeyPanel";

// Points segment columns to skip entirely
const POINTS_SKIP = new Set(["FPG AVG"]);

// Points segment columns moved to end of unified table (after other-segment columns)
const POINTS_COLS_LAST = new Set(["Fouls", "Games"]);

// Display name overrides for Points segment simple columns
const POINTS_DISPLAY = { Total: "PTS", Fouls: "PF", Games: "GP" };

// Display name for first column of each non-Points segment
const SEGMENT_FIRST_COL_DISPLAY = {
  Assists: "AST",
  Rebounds: "REB",
  Steals: "STL",
  Blocks: "BLK",
  Turnovers: "TO",
};

const SEGMENT_ORDER = ["Assists", "Rebounds", "Steals", "Blocks", "Turnovers"];

// Extra columns pulled from a segment beyond its first column, inserted right after it.
// shortName is the per-game column; totalShortName is the matching season-total column
// swapped in when the Total toggle is active.
const SEGMENT_EXTRA_COLS = {
  Rebounds: [
    { shortName: "ORPG", totalShortName: "OR Total", label: "OREB" },
    { shortName: "DRPG", totalShortName: "DR Total", label: "DREB" },
  ],
};

function getPercentageLabels(shortName) {
  const s = shortName.toUpperCase();
  if (s.includes("FGM") || (s.startsWith("FG") && !s.startsWith("FT"))) return ["FG-M", "FG-A", "FG%"];
  if (s.includes("3P")) return ["3PM", "3PA", "3PT%"];
  if (s.includes("FTM") || s.includes("FT")) return ["FTM", "FTA", "FT%"];
  return [`${shortName}-M`, `${shortName}-A`, `${shortName}%`];
}

function getPercentageFullNames(shortName) {
  const s = shortName.toUpperCase();
  if (s.includes("FGM") || (s.startsWith("FG") && !s.startsWith("FT")))
    return ["Field goals made", "Field goals attempted", "Field goal percentage"];
  if (s.includes("3P")) return ["Three-pointers made", "Three-pointers attempted", "Three-point percentage"];
  if (s.includes("FTM") || s.includes("FT"))
    return ["Free throws made", "Free throws attempted", "Free throw percentage"];
  return [`${shortName} made`, `${shortName} attempted`, `${shortName} percentage`];
}

function buildColumnsAndRows(segments, statMode = "perGame") {
  if (!segments?.length) return { columns: [], mergedRows: [], headColumns: [] };

  const total = statMode === "total";
  const segMap = {};
  for (const seg of segments) segMap[seg.title] = seg;

  const pointsSeg = segMap["Points"] || segments[0];
  const sampleRow = pointsSeg.rows[0];

  const colsFirst = [];
  const colsMiddle = [];
  const colsLast = [];

  // Points segment columns
  for (let i = 0; i < pointsSeg.columns.length; i++) {
    const col = pointsSeg.columns[i];
    if (POINTS_SKIP.has(col.shortName)) continue;
    // The standalone "Total" (PTS) column is folded into the swappable PPG/PTS column below.
    if (col.shortName === "Total") continue;

    // PPG carries the points value and swaps to season total (PTS) in Total mode.
    if (col.shortName === "PPG") {
      colsFirst.push({
        key: "PPG",
        label: total ? "PTS" : "PPG",
        fullName: total ? "Total points" : "Points per game",
        cellKey: total ? "Points:Total" : "Points:PPG",
        sortValue: (cell) => parseFloat(cell?.value) || 0,
        renderCell: (cell) => cell?.value ?? "0",
        isDefaultSort: true,
      });
      continue;
    }

    const cellKey = `Points:${col.shortName}`;
    const isLast = POINTS_COLS_LAST.has(col.shortName);
    const target = isLast ? colsLast : colsFirst;
    const sampleCell = sampleRow?.cells[i];

    if (sampleCell?.statType === "percentage") {
      const [mLabel, aLabel, pLabel] = getPercentageLabels(col.shortName);
      const [mName, aName, pName] = getPercentageFullNames(col.shortName);
      target.push({
        key: `${col.shortName}_made`,
        label: mLabel,
        fullName: mName,
        cellKey,
        sortValue: (cell) => cell?.made ?? -Infinity,
        renderCell: (cell) => (cell?.made != null ? cell.made : "—"),
        isDefaultSort: false,
      });
      target.push({
        key: `${col.shortName}_total`,
        label: aLabel,
        fullName: aName,
        cellKey,
        sortValue: (cell) => cell?.total ?? -Infinity,
        renderCell: (cell) => (cell?.total != null ? cell.total : "—"),
        isDefaultSort: false,
      });
      target.push({
        key: `${col.shortName}_pct`,
        label: pLabel,
        fullName: pName,
        cellKey,
        sortValue: (cell) => (cell?.total > 0 ? cell.made / cell.total : -Infinity),
        renderCell: (cell) => (cell?.total > 0 ? `${Math.round((cell.made / cell.total) * 100)}%` : "—"),
        isDefaultSort: false,
      });
    } else {
      target.push({
        key: col.shortName,
        label: POINTS_DISPLAY[col.shortName] || col.shortName,
        fullName: col.name || col.shortName,
        cellKey,
        sortValue: (cell) => parseFloat(cell?.value) || 0,
        renderCell: (cell) => cell?.value ?? "0",
        isDefaultSort: col.shortName === "PPG",
      });
    }
  }

  // First column from each other segment (in defined order), plus any configured extra columns
  for (const segTitle of SEGMENT_ORDER) {
    const seg = segMap[segTitle];
    if (!(seg?.columns?.length > 0)) continue;

    // Per-game first column (e.g. RPG/APG) swaps to the segment's "Total" column in Total mode.
    const col = seg.columns[0];
    const hasTotal = seg.columns.some((c) => c.shortName === "Total");
    const showTotal = total && hasTotal;
    colsMiddle.push({
      key: `${segTitle}_0`,
      label: SEGMENT_FIRST_COL_DISPLAY[segTitle] || col.shortName,
      fullName: showTotal ? `Total ${segTitle.toLowerCase()}` : col.name || col.shortName,
      cellKey: showTotal ? `${segTitle}:Total` : `${segTitle}:${col.shortName}`,
      sortValue: (cell) => parseFloat(cell?.value) || 0,
      renderCell: (cell) => cell?.value ?? "0",
      isDefaultSort: false,
    });

    for (const extra of SEGMENT_EXTRA_COLS[segTitle] ?? []) {
      const perGameCol = seg.columns.find((c) => c.shortName === extra.shortName);
      const totalCol = seg.columns.find((c) => c.shortName === extra.totalShortName);
      const activeCol = total ? totalCol : perGameCol;
      if (!activeCol) continue;
      colsMiddle.push({
        key: `${segTitle}_${extra.shortName}`,
        label: extra.label,
        fullName: activeCol.name || extra.label,
        cellKey: `${segTitle}:${activeCol.shortName}`,
        sortValue: (cell) => parseFloat(cell?.value) || 0,
        renderCell: (cell) => cell?.value ?? "0",
        isDefaultSort: false,
      });
    }
  }

  const allColumns = [...colsFirst, ...colsMiddle, ...colsLast];

  // Build player map — keyed by headCells joined (player + team)
  const playerMap = new Map();

  for (const row of pointsSeg.rows) {
    const key = row.headCells.join("|");
    const cellsByKey = {};
    pointsSeg.columns.forEach((col, i) => {
      cellsByKey[`Points:${col.shortName}`] = row.cells[i];
    });
    playerMap.set(key, {
      headCells: row.headCells,
      highlighted: row.highlighted,
      collapsible: row.collapsible,
      cellsByKey,
    });
  }

  for (const seg of segments) {
    if (seg.title === pointsSeg.title) continue;
    for (const row of seg.rows) {
      const key = row.headCells.join("|");
      if (!playerMap.has(key)) {
        playerMap.set(key, {
          headCells: row.headCells,
          highlighted: row.highlighted,
          collapsible: row.collapsible,
          cellsByKey: {},
        });
      }
      const entry = playerMap.get(key);
      seg.columns.forEach((col, i) => {
        entry.cellsByKey[`${seg.title}:${col.shortName}`] = row.cells[i];
      });
    }
  }

  return {
    columns: allColumns,
    mergedRows: Array.from(playerMap.values()),
    headColumns: pointsSeg.headColumns || [],
  };
}

// Rows ranked beyond this position are hidden behind a "Show More" toggle.
const COLLAPSE_THRESHOLD = 20;

const STAT_KEY_FOOTNOTE =
  "Percentages use made / attempted. Per-game mode divides totals by games played where relevant.";

// Stat key descriptions are taken from each column's fullName; these override a few for clearer copy.
const STAT_KEY_DESCRIPTION_OVERRIDES = { GP: "Games played", PF: "Personal fouls" };

export default function UnifiedLeaderboardTable({ segments, title, statMode = "perGame", csvFilename }) {
  const { columns, mergedRows, headColumns } = useMemo(
    () => buildColumnsAndRows(segments, statMode),
    [segments, statMode],
  );

  // Default sorting is PPG - Points per Game by descending
  const defaultSortKey = columns.find((c) => c.isDefaultSort)?.key ?? columns[0]?.key ?? "PPG";
  const [sortKey, sortKeySet] = useState("PPG");
  const [sortDir, sortDirSet] = useState("desc");
  const [collapsed, collapsedSet] = useState(true);
  const [statKeyOpen, statKeyOpenSet] = useState(false);

  // Derive the Stat key straight from the rendered columns so the terms always match the table
  // headers (including the per-game/total label swaps) and never drift out of sync.
  const statKeyEntries = columns.map((col) => ({
    term: col.label,
    description: STAT_KEY_DESCRIPTION_OVERRIDES[col.label] ?? col.fullName,
  }));

  const effectiveSortKey = columns.find((c) => c.key === sortKey) ? sortKey : defaultSortKey;
  const sortCol = columns.find((c) => c.key === effectiveSortKey);

  // Players who never took the court (0 games played) are dropped from the leaderboard entirely.
  const gpCol = columns.find((c) => c.label === "GP");
  const qualifiedRows = useMemo(() => {
    if (!gpCol) return mergedRows;
    return mergedRows.filter((row) => (parseFloat(row.cellsByKey[gpCol.cellKey]?.value) || 0) > 0);
  }, [mergedRows, gpCol]);

  const sortedRows = useMemo(() => {
    if (!sortCol) return qualifiedRows;
    return [...qualifiedRows].sort((a, b) => {
      const av = sortCol.sortValue(a.cellsByKey[sortCol.cellKey]);
      const bv = sortCol.sortValue(b.cellsByKey[sortCol.cellKey]);
      return sortDir === "desc" ? bv - av : av - bv;
    });
  }, [qualifiedRows, sortCol, sortDir]);

  // Competition ranking over players with a non-zero value in the sorted stat. Players with 0 in
  // that stat still appear (they've played games) but show "—" instead of a rank number.
  const rankedRows = useMemo(() => {
    let rank = 0;
    let rankedCount = 0;
    let prevValue = null;
    return sortedRows.map((row) => {
      const value = sortCol ? sortCol.sortValue(row.cellsByKey[sortCol.cellKey]) : 0;
      if (value > 0) {
        rankedCount += 1;
        if (prevValue === null || value !== prevValue) rank = rankedCount;
        prevValue = value;
        return { ...row, rank };
      }
      return { ...row, rank: null };
    });
  }, [sortedRows, sortCol]);

  // Export every ranked row (not just the visible/collapsed subset), following the current sort order.
  const handleDownloadCsv = () => {
    const header = ["#", ...headColumns, ...columns.map((col) => col.label)];
    const rows = rankedRows.map((row) => [
      row.rank,
      ...row.headCells,
      ...columns.map((col) => col.renderCell(row.cellsByKey[col.cellKey])),
    ]);
    downloadCsv(csvFilename || title || "stats", header, rows);
  };

  // Collapse by the current sort order: keep the top rows (plus the highlighted user row) when collapsed.
  const hasCollapsible = rankedRows.length > COLLAPSE_THRESHOLD;
  const visibleRows =
    collapsed && hasCollapsible ? rankedRows.filter((row, i) => i < COLLAPSE_THRESHOLD || row.highlighted) : rankedRows;

  const handleSort = (key) => {
    if (key === effectiveSortKey) {
      sortDirSet((d) => (d === "desc" ? "asc" : "desc"));
    } else {
      sortKeySet(key);
      sortDirSet("desc");
    }
  };

  return (
    <div style={{ display: "flex", flexFlow: "column nowrap", gap: SPACING }}>
      <div
        style={{
          display: "flex",
          flexFlow: "row nowrap",
          alignItems: "center",
          justifyContent: "space-between",
          gap: SPACING_HALF,
          paddingBottom: SPACING_HALF,
          borderBottom: `1px solid ${COLOR_GREY_BORDER}`,
        }}
      >
        <div
          style={{
            fontFamily: FONT_FAMILY_GRAPHIK,
            fontWeight: 500,
            fontSize: 16,
            textTransform: "uppercase",
            letterSpacing: 0.4,
            color: COLOR_BLACK,
          }}
        >
          {title}
        </div>
        <div style={{ display: "flex", flexFlow: "row nowrap", alignItems: "center", gap: SPACING_HALF }}>
          <HeaderButton label={statKeyOpen ? "Hide key" : "Stat key"} onClick={() => statKeyOpenSet((open) => !open)} />
          {rankedRows.length > 0 && (
            <HeaderButton label="CSV" icon={<Download style={{ fontSize: 18 }} />} onClick={handleDownloadCsv} />
          )}
        </div>
      </div>

      {statKeyOpen && <StatKeyPanel entries={statKeyEntries} footnote={STAT_KEY_FOOTNOTE} />}

      <StatsTable
        headerColumns={["#", ...headColumns]}
        columns={columns.map((col) => {
          const isActive = col.key === effectiveSortKey;
          return (
            <button
              key={col.key}
              title={col.fullName}
              onClick={() => handleSort(col.key)}
              style={{
                background: "none",
                border: "none",
                cursor: "pointer",
                color: isActive ? COLOR_WHITE : alpha(COLOR_WHITE, 0.6),
                fontFamily: FONT_FAMILY_GRAPHIK,
                fontWeight: isActive ? 600 : 500,
                fontSize: "inherit",
                textTransform: "uppercase",
                padding: 0,
                display: "inline-flex",
                alignItems: "center",
                gap: 1,
                whiteSpace: "nowrap",
              }}
            >
              {col.label}
              <span
                style={{
                  fontSize: "0.75em",
                  lineHeight: 1,
                  color: COLOR_WHITE,
                  visibility: isActive ? "visible" : "hidden",
                }}
              >
                {sortDir === "desc" ? "▼" : "▲"}
              </span>
            </button>
          );
        })}
        color={alpha(COLOR_BLACK, 0)}
      >
        {visibleRows.map((row, rowIndex) => (
          // NOTE: row.highlighted is calculated in LeaderboardTableSegment graphql. But we decide not to show row as highlighted in new leaderboard.
          <StatsTableRow key={row.headCells.join("|")} rowIndex={rowIndex}>
            <StatsTableHeaderCell>{row.rank ?? "—"}</StatsTableHeaderCell>
            {row.headCells.map((cell, i) => (
              <StatsTableHeaderCell key={i}>{cell}</StatsTableHeaderCell>
            ))}
            {columns.map((col) => (
              <StatsTableCell key={col.key}>{col.renderCell(row.cellsByKey[col.cellKey])}</StatsTableCell>
            ))}
          </StatsTableRow>
        ))}
        {hasCollapsible && (
          <StatsTableButtonRow>
            <Button
              fullWidth
              color="inherit"
              onClick={() => collapsedSet(!collapsed)}
              style={{
                textTransform: "uppercase",
                fontFamily: FONT_FAMILY_GRAPHIK,
                fontWeight: 600,
              }}
            >
              {collapsed ? "Show More" : "Show Less"}
            </Button>
          </StatsTableButtonRow>
        )}
      </StatsTable>
    </div>
  );
}
